import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { editionDocumentTypeService } from "../services/editionDocumentTypeService";
import { editionDocumentTypeDisplayId } from "./EditionDocumentTypeCommonStore";
import { EditionDocumentType } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class EditionDocumentTypeViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = editionDocumentTypeService;
  editionDocumentTypeInstance: EditionDocumentType | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadEditionDocumentTypeById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.editionDocumentTypeOne({ id });
    this.editionDocumentTypeInstance = result.editionDocumentTypeOne;
    if (this.editionDocumentTypeInstance) {
      this.loadValues(this.editionDocumentTypeInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.editionDocumentTypeInstance) {
      return editionDocumentTypeDisplayId(
        this.editionDocumentTypeInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EditionDocumentTypeSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.editionDocumentTypeInstance = null;
    this.nameValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelEditionDocumentType() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions(
      "editionDocumentType_update"
    );
  }

  editEditionDocumentType() {
    const id = this.editionDocumentTypeInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/editionDocumentType/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printEditionDocumentType(handlePrint: () => void): void {
    handlePrint();
  }

  *closeEditionDocumentType(): any {
    yield this.close();
  }

  loadValues(instance: EditionDocumentType) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadEditionDocumentTypeById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
